/* eslint-disable @typescript-eslint/no-use-before-define */
import { DatePicker, Form, Input, Modal } from "antd";
import TextArea from "antd/es/input/TextArea";
import { AppButton } from "components/button/AppButton";
import { useCreateCertification } from "features/settings/hooks/useCreateCertification";
import { QUERY_KEY_FOR_TALENT_PROFILE } from "features/settings/hooks/useGetTalentProfile";
import { ICreateCertification } from "features/settings/types";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import DragNDrop from "utils/DragNdrop";
import dayjs from "dayjs";
import {
  QUERY_KEY_FOR_SINGLE_CERTIFICATION,
  useGetSingleCertification,
} from "features/settings/hooks/useGetSingleCertification";

type Snapshot = {
  title: string;
  issuer: string;
  certification_id: string;
  description: string;
  link: string;
  issued_date: string | number | null;
  fileKey: string | null;
};

export const AddCertification = ({
  open,
  handleClose,
  id,
}: IdentifierProps) => {
  const { token, userInfo } = useGetUserInfo();
  const isEdit = Boolean(id);
  const { data, isLoading: isLoadingCert } = useGetSingleCertification({
    id: id as number,
    token,
    enabled: open && isEdit,
  });

  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCreateCertification();

  const [file, setFile] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [issuer, setIssuer] = useState("");
  const [link, setLink] = useState("");
  const [issued_date, setIssued_date] = useState<string | number | null>(null);
  const [certification_id, setCertificationId] = useState("");
  const [description, setDescription] = useState("");
  const [initialSnapshot, setInitialSnapshot] = useState<Snapshot | null>(null);

  const resetLocalState = () => {
    form.resetFields();
    setTitle("");
    setIssuer("");
    setCertificationId("");
    setDescription("");
    setLink("");
    setIssued_date(null);
    setFile(null);
    setInitialSnapshot(null);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!isEdit) {
      resetLocalState();
      return;
    }

    if (!data) {
      return;
    }

    const yearValue = data.issued_date
      ? dayjs(String(data.issued_date)).isValid()
        ? dayjs(String(data.issued_date)).year()
        : Number(data.issued_date) || null
      : null;

    form.setFieldsValue({
      title: data.title,
      issuer: data.issuer,
      certification_id: data.certification_id,
      description: data.description,
      link: data.link,
      file_path: data.file_path,
      issued_date: yearValue ? dayjs().year(yearValue) : undefined,
    });
    setTitle(data.title || "");
    setCertificationId(data.certification_id || "");
    setDescription(data.description || "");
    setLink(data.link || "");
    setIssuer(data.issuer || "");
    setIssued_date(yearValue);
    setFile(data.file_path || null);
    setInitialSnapshot({
      title: data.title || "",
      issuer: data.issuer || "",
      certification_id: data.certification_id || "",
      description: data.description || "",
      link: data.link || "",
      issued_date: yearValue,
      fileKey: data.file_path || null,
    });
  }, [open, isEdit, data, form]);

  const currentFileKey =
    file instanceof File
      ? `new:${file.name}:${file.size}:${file.lastModified}`
      : typeof file === "string"
        ? file
        : null;

  const hasChanges = useMemo(() => {
    if (!isEdit) return true;
    if (!initialSnapshot) return false;

    return (
      title !== initialSnapshot.title ||
      issuer !== initialSnapshot.issuer ||
      certification_id !== initialSnapshot.certification_id ||
      description !== initialSnapshot.description ||
      link !== initialSnapshot.link ||
      String(issued_date ?? "") !== String(initialSnapshot.issued_date ?? "") ||
      currentFileKey !== initialSnapshot.fileKey
    );
  }, [
    isEdit,
    initialSnapshot,
    title,
    issuer,
    certification_id,
    description,
    link,
    issued_date,
    currentFileKey,
  ]);

  const disableFutureDates = (current: dayjs.Dayjs) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  const handleModalClose = () => {
    resetLocalState();
    handleClose(false);
  };

  const handleSubmit = () => {
    const hasExistingFile = Boolean(
      file || (isEdit && (data?.file_path || initialSnapshot?.fileKey)),
    );
    if (!hasExistingFile) {
      openNotification({
        title: "Error",
        state: "error",
        description: "Please upload file",
        duration: 6,
      });
      return;
    }

    const props: ICreateCertification = {
      id: id as number,
      talent_id: userInfo?.talent?.id,
      title,
      certification_id,
      description,
      issuer,
      link,
      file: file instanceof File ? file : undefined,
      issued_date: issued_date != null ? String(issued_date) : undefined,
      token,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response?.data?.error) {
          errorTitle = "Error: " + err.response.data.error;
          const validation = err.response.data.data;
          errorDescription =
            err.response.data.description ||
            (validation
              ? Object.values(validation).flat().join(" ")
              : "Please try again.");
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: () => {
        openNotification({
          title: "Success",
          state: "success",
          description: isEdit ? "Updated Successfully" : "Created Successfully",
          duration: 4.5,
        });
        resetLocalState();
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_TALENT_PROFILE],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_SINGLE_CERTIFICATION, id],
        });
        handleClose();
      },
    });
  };

  const handleManualSubmit = () => {
    form
      .validateFields()
      .then(() => {
        handleSubmit();
      })
      .catch(() => {});
  };

  const handleIssuedDateChange = (date: dayjs.Dayjs | null) => {
    setIssued_date(date ? date.year() : null);
  };

  return (
    <Modal
      title={isEdit ? "Edit Certification" : "Add Certification"}
      open={open}
      onCancel={handleModalClose}
      footer={null}
      width="45rem"
      style={{ top: 10 }}
      destroyOnClose
    >
      <div>
        <Form
          layout="vertical"
          requiredMark={false}
          form={form}
          disabled={isEdit && isLoadingCert}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <Form.Item
              name="title"
              label="Title"
              rules={generalValidationRules}
            >
              <Input
                placeholder="Enter Certification Title"
                onChange={(e) => setTitle(e.target.value)}
              />
            </Form.Item>
            <Form.Item
              name="issuer"
              label="Issuing Organization"
              rules={generalValidationRules}
            >
              <Input
                placeholder="Enter Certification Issuer"
                onChange={(e) => setIssuer(e.target.value)}
              />
            </Form.Item>

            <Form.Item name="issued_date" label="Issued Year (Optional)">
              <DatePicker
                picker="year"
                disabledDate={disableFutureDates}
                className="w-full sm:w-auto"
                format="YYYY"
                onChange={handleIssuedDateChange}
                allowClear
              />
            </Form.Item>

            <Form.Item
              name="certification_id"
              label="Certification ID"
              rules={generalValidationRules}
            >
              <Input
                placeholder="Enter Certification ID"
                onChange={(e) => setCertificationId(e.target.value)}
              />
            </Form.Item>
            <Form.Item
              name="link"
              label="Certificate Url"
              rules={generalValidationRules}
            >
              <Input
                placeholder="Enter Certification verification url"
                onChange={(e) => setLink(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Certification Description"
              rules={generalValidationRules}
              className="col-span-2"
            >
              <TextArea
                placeholder="Enter description"
                rows={4}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Item>
          </div>
        </Form>

        <div className="col-span-2">
          <p>Upload certification file{isEdit ? " (optional if already uploaded)" : ""}</p>
          {isEdit && typeof file === "string" && file && (
            <p className="text-sm text-gray-500 mb-2 truncate">
              Current file on record. Upload a new file only if you want to replace it.
            </p>
          )}
          <DragNDrop onFileSelected={setFile} width="100%" />
        </div>

        {(!isEdit || hasChanges) && (
          <div className="flex pt-8">
            <AppButton
              type="button"
              isLoading={isLoading}
              handleClick={handleManualSubmit}
              label={isEdit ? "Update" : "Submit"}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
